import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Settings, LogOut, Shield } from "lucide-react";
import { trpc } from "../api/trpc";

interface UserAvatarMenuProps {
  avatarUrl: string | null;
  onSignOut: () => void;
}

/**
 * User Avatar Menu Component
 *
 * Displays a 40px circular avatar that opens a dropdown menu
 * with Settings and Sign Out options when clicked.
 *
 * Per Figma design specs:
 * - Avatar: 40px circular with border-black/[0.08]
 * - Dropdown: White bg, rounded-lg, shadow-lg, border #E4DED9
 */
export default function UserAvatarMenu({ avatarUrl, onSignOut }: UserAvatarMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Cosmetic gate — shares the cached getMe query with AdminRoute; the routes + API are the real gate.
  const { data: me } = trpc.auth.getMe.useQuery();
  const isAdmin = me?.role === "ADMIN";

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  const handleSettingsClick = () => {
    setIsOpen(false);
    navigate("/settings");
  };

  const handleAdminClick = () => {
    setIsOpen(false);
    navigate("/admin/books");
  };

  const handleSignOutClick = () => {
    setIsOpen(false);
    onSignOut();
  };

  return (
    <div className="relative" ref={menuRef}>
      {/* Avatar Button - 40px circular per Figma */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative w-10 h-10 rounded-full border border-black/[0.08] overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#FF6900] focus:ring-offset-2 transition-opacity hover:opacity-90"
        aria-label="User menu"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {avatarUrl ? (
          <img src={avatarUrl} alt="User avatar" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-[#F4F1EE] flex items-center justify-center">
            <svg
              className="w-5 h-5 text-[#69635C]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
          </div>
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-[#E4DED9] py-1 z-50"
          role="menu"
          aria-orientation="vertical"
        >
          {isAdmin && (
            <button
              onClick={handleAdminClick}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-[#69635C] hover:bg-[#F4F1EE] transition-colors"
              role="menuitem"
            >
              <Shield className="w-5 h-5 text-[#BFB6AE]" />
              <span className="font-medium text-sm">Admin</span>
            </button>
          )}
          <button
            onClick={handleSettingsClick}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-[#69635C] hover:bg-[#F4F1EE] transition-colors"
            role="menuitem"
          >
            <Settings className="w-5 h-5 text-[#BFB6AE]" />
            <span className="font-medium text-sm">Settings</span>
          </button>
          <button
            onClick={handleSignOutClick}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-[#69635C] hover:bg-[#F4F1EE] transition-colors"
            role="menuitem"
          >
            <LogOut className="w-5 h-5 text-[#BFB6AE]" />
            <span className="font-medium text-sm">Sign Out</span>
          </button>
        </div>
      )}
    </div>
  );
}
